// ── Chrome — общая рамка разделов: шапка (TopBar) и всплывающие меню (PopMenu) ──
// Один стиль на всех экранах и размерах (макет «вариант A»): плоские поверхности,
// тонкие рамки, синий акцент. Кнопки не меньше 40 px (на телефоне 44), чтобы
// по ним было легко попасть пальцем.
//
// TopBar: на компьютере — поиск, язык и аккаунт; на телефоне — логотип и название
// раздела, язык и аккаунт (поиск открывается значком). Высота публикуется в
// CSS-переменной --ac-top-h: липкие блоки страницы встают под шапку.
// PopMenu: на компьютере — меню рядом с кнопкой, на телефоне — лист снизу.
// Закрывается кнопкой «Назад» телефона, Esc и нажатием мимо.

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SANS, themeKit } from './themes';
import Icon from './Icon';
import useBackClose from './ui/useBackClose';
import { Sheet } from './ui/Dialog';
import UiIconButton from './ui/IconButton';
import { fieldSurface, floatSurface } from './ui/surface';

import { t, uiLocale } from './i18n';
export const TOPBAR_H = 56;

// Логотип «A» — один на всё приложение (боковое меню, шапка, вход). Обычно синий квадрат;
// в теме с игральной картой (C.face, «Аркада») — туз: кремовая карта с тёмной обводкой,
// красная «A» пиксельным шрифтом и червонка, чуть наклонена и светится, как карта в игре.
export function Brand({ C, size = 28 }) {
  const logo = themeKit(C).logo;
  if (logo === 'diamond') {
    // «Колода»: кремовая карта с красной бубной, как на макете
    return (
      <span aria-hidden="true" style={{ display: 'inline-flex', flexShrink: 0, borderRadius: Math.max(3, Math.round(size * 0.14)), boxShadow: '0 2px 0 rgba(0,0,0,.35)' }}>
        <svg width={Math.round(size * 0.86)} height={size} viewBox="0 0 24 28">
          <rect x="1" y="1" width="22" height="26" rx="3.5" fill={C.face.bg} stroke={C.face.border} strokeWidth="1.5" />
          <path d="M12 6 L19 14 L12 22 L5 14 Z" fill="#d0403a" />
        </svg>
      </span>
    );
  }
  if (logo === 'ace' && C.face) {
    // Пиксельная «A» и червонка — как надписи на картах в игре; всё рисунком (SVG), без текста.
    const px = (rows, x0, y0, k, fill) => rows.flatMap((row, y) => [...row].map((c, x) => (c === 'X'
      ? <rect key={`${x0}${y0}${x}-${y}`} x={x0 + x * k} y={y0 + y * k} width={k} height={k} fill={fill} /> : null)));
    return (
      <span aria-hidden="true" style={{ display: 'inline-flex', flexShrink: 0, rotate: '-6deg', borderRadius: Math.max(3, Math.round(size * 0.14)),
        boxShadow: `0 2px 0 ${C.face.text}, 0 0 12px -2px rgba(0,157,255,.75)` }}>
        <svg width={Math.round(size * 0.78)} height={size} viewBox="0 0 22 28">
          <rect x="1" y="1" width="20" height="26" rx="3.5" fill={C.face.bg} stroke={C.face.text} strokeWidth="2" />
          {px(['.XXX.', 'X...X', 'X...X', 'XXXXX', 'X...X', 'X...X'], 6, 4, 2, '#e0342b')}
          {px(['.X.X.', 'XXXXX', '.XXX.', '..X..'], 7.5, 18, 1.4, '#e0342b')}
        </svg>
      </span>
    );
  }
  return (
    <span aria-hidden="true" style={{ width: size, height: size, borderRadius: Math.round(size * 0.28), background: C.blue,
      color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      fontFamily: SANS, fontWeight: 700, fontSize: Math.round(size * 0.55), lineHeight: 1 }}>A</span>
  );
}

// Кнопка-значок с рамкой (как на макете).
export function IconButton({ C, icon, label, onClick, size = 40, active, style, children, ...rest }) {
  // Тот же значок, что ui/IconButton (обводка): в «Аркаде» — объёмный, с кристальной гранью.
  return (
    <UiIconButton C={C} variant="outline" icon={icon} label={label} onClick={onClick} size={size} active={active} iconSize={18} style={style} {...rest}>
      {children}
    </UiIconButton>
  );
}

// Кнопка «Назад» Android и Esc закрывают верхнее окно (общий стек — src/ui/useBackClose.js).
export { useBackClose };

// Положение меню у кнопки, не выходя за края экрана: под кнопкой, если там хватает
// места (или там его больше), иначе над ней. maxHeight — сколько есть до края: длинное
// меню прокручивается внутри, а не уходит за низ экрана (раньше нижние пункты были не достать).
export function anchorOf(el, width = 240) {
  const r = el?.getBoundingClientRect?.();
  if (!r) return { top: 80, left: 16 };
  const H = window.innerHeight, gap = 6, edge = 8;
  const left = Math.max(edge, Math.min(r.right - width, window.innerWidth - width - edge, r.left));
  const below = H - r.bottom - gap - edge, above = r.top - gap - edge;
  return below >= 240 || below >= above
    ? { top: r.bottom + gap, left, maxHeight: Math.max(120, below) }
    : { bottom: H - r.top + gap, left, maxHeight: Math.max(120, above) };
}

// items: [{icon, label, hint, onClick, danger, checked, disabled}] или 'divider' или {header}.
export function PopMenu({ C, at, sheet, title, items, onClose, width = 240 }) {
  // Лист снизу сам закрывается «Назад»/Esc и прячет мини-плеер (ui/Dialog).
  useBackClose(!sheet, onClose);

  const list = items.filter(Boolean).map((it, i) => {
    if (it === 'divider') return <div key={`d${i}`} style={{ height: 1, background: C.borderSoft, margin: '4px 6px' }} />;
    if (it.header) return (
      <div key={`h${i}`} style={{ padding: sheet ? '6px 14px 8px' : '6px 12px 6px', fontSize: 12.5, color: C.faint,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.header}</div>
    );
    return (
      <button key={it.label} role="menuitem" disabled={it.disabled} className="ac-ibtn"
        onClick={() => { onClose(); it.onClick?.(); }}
        style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', minHeight: sheet ? 48 : 40,
          padding: sheet ? '8px 14px' : '6px 12px', background: 'none', border: 'none', borderRadius: 8,
          cursor: it.disabled ? 'default' : 'pointer', opacity: it.disabled ? 0.5 : 1, fontFamily: SANS,
          fontSize: sheet ? 15.5 : 14, color: it.danger ? C.danger : C.text, textAlign: 'left' }}>
        {it.icon && <span style={{ display: 'inline-flex', color: it.danger ? C.danger : C.mid }}><Icon n={it.icon} s={18} /></span>}
        <span style={{ flex: 1, minWidth: 0 }}>
          {/* Длинный пункт (испанский, пиксельный шрифт) — в две строки, а не «…». */}
          <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere', lineHeight: 1.3 }}>{it.label}</span>
          {it.hint && <span style={{ display: 'block', fontSize: 12, color: C.faint, marginTop: 1 }}>{it.hint}</span>}
        </span>
        {it.checked && <span style={{ color: C.blueDark, display: 'inline-flex' }}><Icon n="check" s={16} /></span>}
      </button>
    );
  });

  // На телефоне — общий лист снизу (ui/Sheet): тот же вид, что у остальных листов.
  if (sheet) {
    return (
      <Sheet C={C} title={title} onClose={onClose} padded={false} bodyStyle={{ padding: '0 8px 10px' }}>
        <div role="menu" aria-label={title}>{list}</div>
      </Sheet>
    );
  }
  // Меню выносится в <body>: у шапки размытый фон, а он «запирает» fixed-элементы внутри себя.
  return createPortal(
    <>
      <div onClick={onClose} onContextMenu={(e) => { e.preventDefault(); onClose(); }} style={{ position: 'fixed', inset: 0, zIndex: 10049 }} />
      <div role="menu" aria-label={title} data-panel=""
        style={{ position: 'fixed', zIndex: 10050, width, maxHeight: '70vh', overflowY: 'auto', ...at,
          ...floatSurface(C), borderRadius: 10, padding: 4,
          boxShadow: `0 12px 32px -12px ${C.shadowMd}`, fontFamily: SANS }}>
        {title && <div style={{ padding: '8px 12px 6px', fontSize: 12.5, fontWeight: 600, color: C.faint,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>}
        {list}
      </div>
    </>,
    document.body
  );
}

// Поле поиска (одинаковое в шапке компьютера и в строке поиска телефона).
// hotkey — Ctrl+K / ⌘K ставит курсор в поиск, подсказка видна в пустом поле («Колода»).
const MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');
function SearchField({ C, value, onChange, placeholder, autoFocus, onEscape, hotkey = false }) {
  const ref = useRef(null);
  const [focus, setFocus] = useState(false);
  useEffect(() => {
    if (!hotkey) return undefined;
    const on = (e) => { if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K' || e.code === 'KeyK')) { e.preventDefault(); ref.current?.focus(); } };
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  }, [hotkey]);
  return (
    <div style={{ position: 'relative', flex: '1 1 auto', minWidth: 0 }}>
      <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: C.faint,
        display: 'inline-flex', pointerEvents: 'none' }}><Icon n="search" s={16} /></span>
      <input ref={ref} value={value} autoFocus={autoFocus} onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
        onKeyDown={(e) => { if (e.key === 'Escape') { if (value) onChange(''); else onEscape?.(); } }}
        placeholder={placeholder} aria-label={placeholder} type="search" enterKeyHint="search"
        style={{ width: '100%', boxSizing: 'border-box', height: 40, ...fieldSurface(C), color: C.text,
          borderColor: focus ? C.blue : C.border, borderRadius: 8, padding: '0 36px', fontSize: 14,
          fontFamily: SANS, outline: 'none', boxShadow: focus ? `0 0 0 3px ${C.progressTint}` : 'none' }} />
      {hotkey && !value && !focus && (
        // подсказка лежит поверх поля нарочно (клики проходят в поле) — cover для автопроверки
        <kbd aria-hidden="true" data-layout-allow="cover" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: C.faint,
          fontFamily: SANS, pointerEvents: 'none' }}>{MAC ? '⌘K' : 'Ctrl K'}</kbd>
      )}
      {value && (
        <button onClick={() => { onChange(''); ref.current?.focus(); }} aria-label={t("Очистить поиск")} className="ac-ibtn"
          style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', width: 32, height: 32,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none',
            color: C.mid, cursor: 'pointer', borderRadius: 6 }}><Icon n="close" s={14} /></button>
      )}
    </div>
  );
}

// Шапка раздела.
//   phone       — телефон (иначе компьютер / планшет);
//   title       — название раздела (на телефоне — в шапке);
//   onBack      — показать «←» вместо логотипа (папка);
//   search      — {value, onChange, placeholder} или null;
//   lang        — готовый выбор языка;
//   accountItems — пункты меню аккаунта (PopMenu items), email — подпись в меню.
export function TopBar({ C, phone, title, onBack, search, lang, accountItems, email, onNew }) {
  const [menu, setMenu] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const showSearchRow = phone && search && (searchOpen || !!search.value);

  useEffect(() => {
    const root = document.documentElement.style;
    root.setProperty('--ac-top-h', `${TOPBAR_H + (showSearchRow ? 52 : 0)}px`);
    return () => root.removeProperty('--ac-top-h');
  }, [showSearchRow]);

  const openAccount = (e) => setMenu(phone ? { sheet: true } : { at: anchorOf(e.currentTarget, 260) });
  // «Колода» (layout hand): шапка как на макете — короткий поиск с ⌘K, по центру дата,
  // справа язык и кружок с первой буквой почты.
  const hand = themeKit(C).layout === 'hand';
  const accountBtn = hand ? (
    <button onClick={openAccount} aria-label={t("Аккаунт и настройки")} title={t("Аккаунт и настройки")} aria-haspopup="menu" aria-expanded={!!menu}
      className="ac-ibtn" style={{ width: 36, height: 36, flexShrink: 0, borderRadius: 8, background: C.surface, border: `1px solid ${C.border}`,
        color: C.text, fontFamily: SANS, fontSize: 14, fontWeight: 700, cursor: 'pointer', padding: 0 }}>
      {(email || '?').trim().charAt(0).toUpperCase()}
    </button>
  ) : (
    <IconButton C={C} icon="user" label={t("Аккаунт и настройки")} onClick={openAccount} size={phone ? 40 : 40}
      aria-haspopup="menu" aria-expanded={!!menu} />
  );
  const today = (() => {
    const d = new Date();
    return `${d.toLocaleDateString(uiLocale(), { day: 'numeric', month: 'long' })} · ${d.toLocaleDateString(uiLocale(), { weekday: 'long' })}`;
  })();

  return (
    <header data-surface="glass" style={{ position: 'sticky', top: 0, zIndex: 20, background: C.glassHdr,
      backdropFilter: 'saturate(1.4) blur(12px)', WebkitBackdropFilter: 'saturate(1.4) blur(12px)',
      borderBottom: `1px solid ${C.border}`, fontFamily: SANS }}>
      <div style={{ height: TOPBAR_H, boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 8,
        padding: phone ? '0 12px 0 16px' : '0 24px', maxWidth: phone ? undefined : hand ? 1180 : 880, margin: '0 auto' }}>
        {phone ? (
          <>
            {onBack ? (
              <IconButton C={C} icon="arrowLeft" label={t("Назад")} onClick={onBack} style={{ border: 'none', marginLeft: -8 }} />
            ) : <Brand C={C} size={28} />}
            <h1 style={{ margin: '0 0 0 4px', flex: 1, minWidth: 0, fontSize: 18, fontWeight: 600, color: C.text,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', letterSpacing: '-0.01em' }}>{title}</h1>
            {search && !showSearchRow && (
              <IconButton C={C} icon="search" label={t("Поиск")} onClick={() => setSearchOpen(true)} style={{ border: 'none' }} />
            )}
            {lang}
            {onNew && (
              <UiIconButton C={C} variant="filled" size={40} label={t("Создать")} onClick={onNew} style={{ borderRadius: 10 }}>
                <Icon n="plus" s={20} sw={2.2} />
              </UiIconButton>
            )}
            {accountBtn}
          </>
        ) : (
          <>
            {search ? (
              <div style={{ flex: hand ? '0 1 260px' : '1 1 auto', maxWidth: hand ? 260 : 560, display: 'flex' }}>
                <SearchField C={C} value={search.value} onChange={search.onChange} placeholder={search.placeholder} hotkey={hand} />
              </div>
            ) : (
              <h1 style={{ margin: 0, flex: '1 1 auto', minWidth: 0, fontSize: 18, fontWeight: 600, color: C.text,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</h1>
            )}
            {hand
              ? <span style={{ flex: '1 1 0', minWidth: 0, textAlign: 'center', fontSize: 12.5, color: C.faint, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{today}</span>
              : <span style={{ flex: '1 1 0', minWidth: 0 }} />}
            {lang}
            {accountBtn}
          </>
        )}
      </div>
      {showSearchRow && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '0 12px 12px 16px' }}>
          <SearchField C={C} value={search.value} onChange={search.onChange} placeholder={search.placeholder}
            autoFocus={searchOpen && !search.value} onEscape={() => setSearchOpen(false)} />
          <button onClick={() => { search.onChange(''); setSearchOpen(false); }} className="ac-ibtn"
            style={{ height: 40, padding: '0 10px', background: 'none', border: 'none', borderRadius: 8, color: C.blueDark,
              fontFamily: SANS, fontSize: 14, fontWeight: 500, cursor: 'pointer', flexShrink: 0 }}>{t("Отмена")}</button>
        </div>
      )}
      {menu && (
        <PopMenu C={C} sheet={menu.sheet} at={menu.at} width={260} title={phone ? t("Аккаунт") : undefined}
          items={[email && { header: email }, ...(accountItems || [])]} onClose={() => setMenu(null)} />
      )}
    </header>
  );
}
